'use client'

import { FC } from 'react'
import { SurveySheetHost } from '../modal/ModalSurvey'
import SurveyActionBar from './SurveyActionBar'
import type { SurveySheetState } from './useSurveySheet'

/**
 * 動画紐づきアンケート（ADR-0014/0015）を「動画の外の白いパネル」に描画する版。
 *
 * BOX 型の PC（2カラム）で使う。左カラムは縦型動画で面積が狭く、下から迫り出すシート
 * （SPSurveyLayer / SurveySheetHost）だと出演者が隠れてしまう。BOX は「見るのは左、
 * 決めるのは右」を原則にしているので（選択肢も右パネル・ADR-0016）、アンケートも同じく
 * 右パネル側で答えさせる。
 *
 * 親は `position: relative` なパネルの中にこれを置く。自身は絶対配置でパネルを基準に
 * 位置を取り、待機中（任意設問のオプトイン）は下部のピルだけを出す。
 */
const PanelSurvey: FC<{ survey: SurveySheetState }> = ({ survey }) => {
  // 展開中はパネル全面を白いシートで覆う（必須設問は動画も一時停止されている）。
  // 上に余白を作らず、ブランドヘッダーをパネル最上部まで届かせる。
  //
  // パネル右上にはモーダルの閉じるボタン（z-40）が重なるため、シート自身の閉じるボタンと
  // 潰し合わないよう重なり順で解決する:
  // - 任意設問: シートが自前の閉じるボタンを持つので、シートを上（z-50）に出して
  //   モーダルのボタンを覆う。シートを閉じればモーダルのボタンがまた現れる。
  // - 必須設問: シートに閉じるボタンが無い（SurveySheet の !isForced）。モーダルの
  //   ボタンを上に残さないと視聴者が抜け出せなくなるので、シートは z-30 のままにする。
  //   この場合ヘッダー右上は空いているのでボタン同士は衝突しない。
  if (survey.expanded && survey.activeQuestion) {
    return (
      <div
        data-testid="panel-survey-sheet"
        className={`pointer-events-auto absolute inset-0 bg-white ${survey.isForced ? 'z-30' : 'z-50'}`}
      >
        <div className="h-full [&_.moovy-survey-sheet]:!rounded-none">
          <SurveySheetHost survey={survey} />
        </div>
      </div>
    )
  }

  // 任意設問は動画を止めずにパネル下部でオプトインを案内する。
  // ボタン本体は共通の SurveyActionBar を使う（動画個別ページ・TILE/SLIDER の script と同一）。
  // ここが持つのは配置（パネル下端の追従スタック）だけで、見た目は一切上書きしない。
  if (survey.showTab) {
    return (
      <div data-testid="panel-survey-optin" className="pointer-events-none absolute inset-x-0 bottom-0 z-30">
        <div className="pointer-events-auto px-10 pb-[35px]">
          <SurveyActionBar total={survey.progress.total} completed={false} available onOpen={survey.onOpenTab} />
        </div>
      </div>
    )
  }

  return null
}

export default PanelSurvey
